import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Form, Row, Col, Button, Table, Card, Badge } from 'react-bootstrap';
import Select from 'react-select';
import { BASE_URL } from '../../services/Api';
import { authHeader } from '../../services/auth-header';
import { debounce } from 'lodash';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

const ProductionOrderCreate = () => {
  const navigate = useNavigate();
  const [jobnumber, setjobnumber] = useState('');
  const [partno, setpartno] = useState('');
  const [size, setsize] = useState('');
  const [bomname, setBomName] = useState('');

  const [customerOptions, setCustomerOptions] = useState([]);
  const [staffOptions, setStaffOptions] = useState([]);
  const [billOfMaterialOptions, setBillOfMaterialOptions] = useState([]);
  const [customerSearch, setCustomerSearch] = useState(encodeURIComponent(" "));
  const [staffSearch, setStaffSearch] = useState(encodeURIComponent(" "));
  const [billOfMaterialSearch, setBillOfMaterialSearch] = useState(encodeURIComponent(" "));
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState([]);
  const [selectedBillOfMaterial, setSelectedBillOfMaterial] = useState(null);
  const [productionorderdate, setProductionOrderDate] = useState(new Date().toISOString().split('T')[0]);
  const [inhousestartdate, setInhouseStartDate] = useState('');
  const [inhouseenddate, setInhouseEndDate] = useState('');
  const [outwardjobworkdate, setOutwardJobworkDate] = useState('');
  const [inwardjobworkdate, setInwardJobworkDate] = useState('');
  const [status, setProdOrderStatus] = useState('Open');
  const [tableRows, setTableRows] = useState([]);

  // ✅ For image upload
  const [createdOrderId, setCreatedOrderId] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [showImageUpload, setShowImageUpload] = useState(false); // ✅ Naya state

  const getCustomerData = async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}customer/page/0/10/asc/id/${customerSearch}`,
        { headers: authHeader() }
      );
      const customers = response.data.Index || response.data.data;
      setCustomerOptions(
        customers.map((customer) => ({
          value: customer.id,
          label: customer.companyname,
        }))
      );
    } catch (error) {
      console.error('Error fetching customer data', error);
    }
  };

  const getStaffData = async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}staff/page/0/10/asc/id/${staffSearch}`,
        { headers: authHeader() }
      );
      const staffMembers = response.data.Index || response.data.data;
      setStaffOptions(
        staffMembers.map((staff) => ({
          value: staff.id,
          label: `${staff.firstname} ${staff.lastname}`,
        }))
      );
    } catch (error) {
      console.error('Error fetching staff data', error);
    }
  };

  const getBillOfMaterialData = async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}bom/page/bombytype/0/10/asc/id/pump/${billOfMaterialSearch}`,
        { headers: authHeader() }
      );
      const billOfMaterials = response.data.Index || response.data.data;
      setBillOfMaterialOptions(
        billOfMaterials.map((bom) => ({
          value: bom.id,
          // ✅ Custom label with BOM name + Job Number + Part Number + Size
          label: `${bom.bomname} | JN: ${bom.jobnumber || 'N/A'} | PN: ${bom.partno || 'N/A'} | Size: ${bom.size || 'N/A'}`,
          finalproduct: bom.finalproduct,
          grandtotalpump: bom.grandtotalpump,
          setjobnumber: bom.jobnumber,
          setpartno: bom.partno,
          setsize: bom.size,
        }))
      );
    } catch (error) {
      console.error('Error fetching BOM data', error);
    }
  };

  useEffect(() => {
    getCustomerData();
    getStaffData();
    getBillOfMaterialData();
  }, [customerSearch, staffSearch, billOfMaterialSearch]);

  const handleSearchChangeCustomer = debounce((inputValue) => {
    setCustomerSearch(inputValue);
  }, 200);

  const handleSearchChangeStaff = debounce((inputValue) => {
    setStaffSearch(inputValue);
  }, 200);

  const handleSearchChangeBillOfMaterial = debounce((inputValue) => {
    setBillOfMaterialSearch(inputValue);
  }, 200);

  const handleBillOfMaterialSelect = (selectedOption) => {
    setSelectedBillOfMaterial(selectedOption);
    setjobnumber(selectedOption.setjobnumber || '');
    setpartno(selectedOption.setpartno || '');
    setsize(selectedOption.setsize || '');
    setBomName(selectedOption.label || '');

    setTableRows([
      {
        billOfMaterial: selectedOption,
        remarks: '',
        quantity: 0,
        uom: '',
        finalproduct: selectedOption.finalproduct,
        grandtotalpump: selectedOption.grandtotalpump,
      },
    ]);
  };

  const handleInputChange = (index, field, value) => {
    const updatedRows = [...tableRows];
    updatedRows[index][field] = value;
    setTableRows(updatedRows);
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedImages(files);
  };

  // ✅ Production Order Submit
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!selectedCustomer || !selectedBillOfMaterial || !inhousestartdate) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Fields',
        text: 'Please fill in all required fields.',
      });
      return;
    }

    const Payload = {
      type: "Pump",
      status: status,
      customer: {
        id: selectedCustomer.value
      },
      operator: selectedStaff.map(staff => ({ id: staff.value })),
      popumpitems: tableRows.map(row => ({
        bomproductname: row.billOfMaterial?.label || '',
        quantity: row.quantity,
        uom: row.uom,
        ramarks: row.remarks,
        rate: Number(row.grandtotalpump) || 0,
        finishedbom: {
          id: selectedBillOfMaterial.value
        },
        product: {
          id: selectedBillOfMaterial.finalproduct
        }
      })),
      jobnumber,
      partno,
      size,
      productionorderdate,
      inhousestartdate,
      inhouseenddate,
      outwardjobworkdate,
      inwardjobworkdate
    };

    try {
      const response = await axios.post(
        `${BASE_URL}productionorder`,
        Payload,
        {
          headers: {
            ...authHeader(),
            'Content-Type': 'application/json' // ✅ JSON ke liye sahi header
          }
        }
      );
      
      console.log('Created Order Response:', response.data); // ✅ Debug log
      
      // ✅ ID ko save karo
      const newOrderId = response.data.id;
      setCreatedOrderId(newOrderId);
      setShowImageUpload(true); // ✅ Image upload section show karo

      // ✅ SweetAlert with proper handling
      Swal.fire({
        icon: 'success',
        title: 'Production Order Created Successfully!',
        html: `
          <p>Order ID: <strong>${newOrderId}</strong></p>
          <p>Now you can upload images for this production order.</p>
        `,
        showCancelButton: true,
        confirmButtonText: 'Upload Images',
        cancelButtonText: 'Back to List',
        allowOutsideClick: false,
      }).then((result) => {
        if (result.isConfirmed) {
          // User ne "Upload Images" par click kiya - image upload section already visible hai
          // Scroll to image upload section
          document.querySelector('.image-upload-section')?.scrollIntoView({ behavior: 'smooth' });
        } else if (result.isDismissed) {
          // User ne cancel kiya ya outside click kiya
          navigate("/Porderindex/1");
        }
      });

    } catch (error) {
      console.error("Error submitting data", error);
      
      // ✅ Better error handling
      let errorMessage = 'Failed to submit data.';
      if (error.response) {
        console.error('Error response:', error.response.data);
        errorMessage = error.response.data?.message || `Server Error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'No response from server. Please check your connection.';
      }
      
      Swal.fire({
        icon: 'error',
        title: 'Submission Failed',
        text: errorMessage,
      });
    }
  };

  const handleImageUpload = async () => {
    if (!createdOrderId) {
      Swal.fire({
        icon: 'warning',
        title: 'No Order',
        text: 'Please submit the production order first.',
      });
      return;
    }

    if (selectedImages.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No Images',
        text: 'Please select images to upload.',
      });
      return;
    }

    const formData = new FormData();
    for (let i = 0; i < selectedImages.length; i++) {
      formData.append("files", selectedImages[i]);
    }

    try {
      console.log('Uploading images for Order ID:', createdOrderId); // ✅ Debug log
      
      const response = await axios.post(
        `${BASE_URL}productionorder/productionorders/images/${createdOrderId}`,
        formData,
        {
          headers: {
            ...authHeader(),
            // ❌ Content-Type hatao - axios automatically set karega multipart/form-data ke liye
            // 'Content-Type': 'multipart/form-data', // ← YE GALAT THA
          }
        }
      );

      console.log('Upload response:', response);
      
      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Images uploaded successfully!',
      }).then(() => {
        navigate("/Porderindex/1");
      });

    } catch (error) {
      console.error("Image upload error", error);

      if (error.response?.status === 413) {
        Swal.fire(
          "File Too Large",
          "Total file size exceeds the limit. Please upload smaller files.",
          "error"
        );
      } else if (error.response?.status === 404) {
        Swal.fire(
          "Not Found",
          `Order ID ${createdOrderId} not found. Please create the order again.`,
          "error"
        );
      } else {
        const errorMessage = error.response?.data?.message || "Image upload failed.";
        Swal.fire("Upload Failed", errorMessage, "error");
      }
    }
  };

  // ✅ Skip upload and go back
  const handleSkipUpload = () => {
    Swal.fire({
      title: 'Skip Image Upload?',
      text: 'You can upload images later from the production order list.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Skip',
      cancelButtonText: 'Stay Here',
    }).then((result) => {
      if (result.isConfirmed) {
        navigate("/Porderindex/1");
      }
    });
  };

  return (
    <div className="Container">
      <Card className="card">
        <Card.Header className="card-header">
          <h6>PRODUCTION ORDER</h6>
          <Link to="/Porderindex/1">
            <button className="button-70">BACK</button>
          </Link>
        </Card.Header>
        <div className="container">
          <br />
          <Form onSubmit={handleSubmit}>
            <Row>
              {/* ✅ BOM Details Showcase - Table ke upar */}
              {selectedBillOfMaterial && (
                <Col md={12} className="mb-3">
                  <Card className="bg-light border-primary">
                    <Card.Body>
                      <h6 className="mb-3 text-primary">📋 Selected BOM Details</h6>
                      <div className="d-flex flex-wrap gap-3">
                        <div className="px-3 py-2 bg-white rounded border">
                          <small className="text-muted">BOM Name</small><br/>
                          <strong>{bomname}</strong>
                        </div>
                        <div className="px-3 py-2 bg-white rounded border">
                          <small className="text-muted">Job Number</small><br/>
                          <Badge bg="success" className="fs-6">{jobnumber || 'N/A'}</Badge>
                        </div>
                        <div className="px-3 py-2 bg-white rounded border">
                          <small className="text-muted">Part Number</small><br/>
                          <Badge bg="info" className="fs-6">{partno || 'N/A'}</Badge>
                        </div>
                        <div className="px-3 py-2 bg-white rounded border">
                          <small className="text-muted">Size</small><br/>
                          <Badge bg="warning" text="dark" className="fs-6">{size || 'N/A'}</Badge>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              )}

              <Col md="6">
                <Form.Group>
                  <Form.Label>Production Order Status</Form.Label>
                  <Form.Select
                    value={status}
                    onChange={(e) => setProdOrderStatus(e.target.value)}
                  >
                    <option value="Open">Open</option>
                    <option value="Ongoing">Ongoing</option>
                    <option value="On Hold">On Hold</option>
                    <option value="Complete">Complete</option>
                  </Form.Select>
                </Form.Group>
              </Col>
             
              <Col md="6">
                <Form.Group>
                  <Form.Label>Production Order Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={productionorderdate}
                    onChange={(e) => setProductionOrderDate(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group>
                  <Form.Label>Customer Name</Form.Label>
                  <Select
                    options={customerOptions}
                    onChange={setSelectedCustomer}
                    onInputChange={handleSearchChangeCustomer}
                    value={selectedCustomer}
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: base => ({ ...base, zIndex: 9999 })
                    }}
                  />
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group>
                  <Form.Label>Operator Name</Form.Label>
                  <Select
                    isMulti
                    options={staffOptions}
                    onChange={setSelectedStaff}
                    onInputChange={handleSearchChangeStaff}
                    value={selectedStaff}
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: base => ({ ...base, zIndex: 9999 })
                    }}
                  />
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group>
                  <Form.Label>Inhouse Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={inhousestartdate}
                    onChange={(e) => setInhouseStartDate(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group>
                  <Form.Label>Inhouse End Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={inhouseenddate}
                    onChange={(e) => setInhouseEndDate(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group>
                  <Form.Label>Outward (Jobwork) Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={outwardjobworkdate}
                    onChange={(e) => setOutwardJobworkDate(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group>
                  <Form.Label>Inward (Jobwork) Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={inwardjobworkdate}
                    onChange={(e) => setInwardJobworkDate(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <br />
            <hr />
            <Row>
              <Col md="12">
                <Form.Group>
                  <Form.Label>Finished Product Name</Form.Label>
                  <Select
                    options={billOfMaterialOptions}
                    onChange={handleBillOfMaterialSelect}
                    onInputChange={handleSearchChangeBillOfMaterial}
                    value={selectedBillOfMaterial}
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: base => ({ ...base, zIndex: 9999 })
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>
            <br /><br />
            <div className="table-responsive">
              <Table>
                <thead>
                  <tr>
                    <th>Product Details</th>
                    <th>Quantity</th>
                    <th>Unit of Measure (UOM)</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((row, index) => (
                    <tr key={index}>
                      <td>{row.billOfMaterial ? row.billOfMaterial.label : ''}</td>
                      <td>
                        <Form.Control
                          className="quantity"
                          type="number"
                          value={row.quantity}
                          onChange={(e) =>
                            handleInputChange(index, 'quantity', parseInt(e.target.value, 10))
                          }
                        />
                      </td>
                      <td>
                        <Form.Select
                          className="uom"
                          value={row.uom}
                          onChange={(e) =>
                            handleInputChange(index, 'uom', e.target.value)
                          }
                        >
                          <option>Select uom</option>
                          <option value="bot">Bot</option>
                          <option value="ctn">Ctn</option>
                          <option value="days">Days</option>
                          <option value="ft">Ft</option>
                          <option value="hrs">Hrs</option>
                          <option value="kg">Kg</option>
                          <option value="grams">Grams</option>
                          <option value="ltr">Ltr</option>
                          <option value="mtr">Mtr</option>
                          <option value="nos">Nos</option>
                          <option value="pil">Pil</option>
                          <option value="pkt">Pkt</option>
                          <option value="set">Set</option>
                          <option value="pcs">Pcs</option>
                        </Form.Select>
                      </td>
                      <td>
                        <textarea
                          rows="2"
                          cols="30"
                          className="custom-textarea"
                          value={row.remarks}
                          onChange={(e) => handleInputChange(index, 'remarks', e.target.value)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            <br />
            <Button type="submit" variant="primary">Submit Production Order</Button>
          </Form>

          {/* ✅ Image Upload Section */}
          {showImageUpload && createdOrderId && (
            <div className="mt-4 image-upload-section">
              <Card className="border-success">
                <Card.Header className="bg-success text-white">
                  <h6>Step 2: Upload Production Order Images</h6>
                </Card.Header>
                <Card.Body>
                  <p className="text-muted">
                    Order ID: <strong>{createdOrderId}</strong> | 
                    Upload images for this production order or{' '}
                    <Link to="/Porderindex/1" className="text-primary">
                      go back to production order list
                    </Link>
                  </p>
                  <Form.Group controlId="formFileMultiple" className="mb-3">
                    <Form.Label>Select Images</Form.Label>
                    <Form.Control
                      type="file"
                      multiple
                      onChange={handleImageChange}
                      accept="image/*"
                    />
                    {selectedImages.length > 0 && (
                      <small className="text-success">
                        {selectedImages.length} file(s) selected
                      </small>
                    )}
                  </Form.Group>
                  <div className="d-flex gap-2">
                    <Button variant="success" onClick={handleImageUpload}>
                      📤 Upload Images
                    </Button>
                    <Button
                      variant="outline-secondary"
                      onClick={handleSkipUpload}
                    >
                      Skip and Go Back
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default ProductionOrderCreate;